/**
 * POS Order History Component
 * 2-day order history lookup for cashiers
 * Features: Search by phone/order#, view details, reprint receipts
 */
import { useState, useEffect } from 'react';
import { 
  Clock, Search, Phone, Receipt, Printer, X, Loader2, 
  User, ShoppingBag, Calendar, CreditCard, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function POSOrderHistory({ staffId, onClose }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchPhone, setSearchPhone] = useState('');
  const [searchOrder, setSearchOrder] = useState('');
  const [reprintingId, setReprintingId] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Fetch initial order history
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async (phone = '', orderNum = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (phone) params.append('phone', phone);
      if (orderNum) params.append('order_number', orderNum);
      params.append('limit', '50');
      
      const res = await api.get(`/pos/order-history?${params.toString()}`, {
        headers: { 'X-Staff-ID': staffId }
      });
      
      setOrders(res.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      toast.error('Failed to load order history');
    }
    setLoading(false);
  };

  const handleSearch = () => {
    fetchOrders(searchPhone, searchOrder);
  };

  const handleClearSearch = () => {
    setSearchPhone('');
    setSearchOrder('');
    fetchOrders();
  };

  const handleReprint = async (orderId) => {
    setReprintingId(orderId);
    try {
      const res = await api.post(`/pos/reprint-receipt/${orderId}`, {}, {
        headers: { 'X-Staff-ID': staffId }
      });
      
      if (res.data.success) {
        toast.success(`Receipt reprinting: Order #${res.data.order_number}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reprint receipt');
    }
    setReprintingId(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  const getPaymentIcon = (method) => {
    switch (method?.toLowerCase()) {
      case 'cash': return '💵';
      case 'card': return '💳';
      case 'mpaisa': return '📱';
      default: return '💰';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="order-history-modal">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Order History</h2>
              <p className="text-sm text-blue-100">Last 2 days • Search & Reprint</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Search Bar */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by phone number..."
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                data-testid="search-phone-input"
              />
            </div>
            <div className="relative flex-1">
              <Receipt className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by order number..."
                value={searchOrder}
                onChange={(e) => setSearchOrder(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                data-testid="search-order-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              {(searchPhone || searchOrder) && (
                <Button variant="outline" onClick={handleClearSearch}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No orders found in the last 2 days</p>
              {(searchPhone || searchOrder) && (
                <p className="text-sm mt-2">Try adjusting your search criteria</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <div 
                  key={order.id}
                  className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden shadow-sm"
                  data-testid={`order-row-${order.id}`}
                >
                  {/* Order Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600/50 transition-colors"
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                          <ShoppingBag className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900 dark:text-white">
                              #{order.order_number || order.id?.slice(-6)}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              Paid
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mt-1">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {order.customer_name}
                            </span>
                            {order.customer_phone && (
                              <span className="flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400">
                                <Phone className="w-3 h-3" />
                                {order.customer_phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900 dark:text-white">
                            ${order.total?.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            {getPaymentIcon(order.payment_method)}
                            {order.payment_method}
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReprint(order.id);
                          }}
                          disabled={reprintingId === order.id}
                          className="text-green-600 hover:text-green-700 border-green-300 hover:border-green-400 hover:bg-green-50"
                          data-testid={`reprint-btn-${order.id}`}
                        >
                          {reprintingId === order.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Printer className="w-4 h-4 mr-1" />
                              Reprint
                            </>
                          )}
                        </Button>
                        
                        {expandedOrder === order.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Date/Time Row */}
                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(order.created_at)}
                      </span>
                      <span>{order.item_count} item(s)</span>
                      <span className="capitalize">{order.order_type}</span>
                      {order.table_number && (
                        <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                          Table {order.table_number}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedOrder === order.id && (
                    <div className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Order Items</h4>
                      <div className="space-y-2">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-700 dark:text-gray-300">
                              {item.quantity}x {item.name}
                              {item.modifiers?.length > 0 && (
                                <span className="text-xs text-gray-500 ml-2">
                                  ({item.modifiers.map(m => m.name || m).join(', ')})
                                </span>
                              )}
                            </span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
                          <span className="text-gray-900 dark:text-white">${order.subtotal?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-400">Tax</span>
                          <span className="text-gray-900 dark:text-white">${order.tax?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold">
                          <span className="text-gray-900 dark:text-white">Total</span>
                          <span className="text-blue-600 dark:text-blue-400">${order.total?.toFixed(2)}</span>
                        </div>
                      </div>
                      {order.staff_name && (
                        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                          Served by: {order.staff_name}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {orders.length} order(s) from the last 2 days
            </p>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
